import {
  type CSSProperties,
  ForwardedRef,
  forwardRef,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { defaultOptions } from "../core/constants";
import { Frame } from "../types";
import { useAnimator, AnimatorHandle } from "../hooks/useAnimator";
import { extractValue, extractItemConfig } from "../core/normalize";

let idCounter = 0;

export interface KpiBoardAnimatorProps {
  frames: Frame[];
  width?: number;
  height?: number;
  padding?: number;
  duration?: number;
  labels?: boolean;
  labelFontSize?: number;
  animateEach?: boolean;
  loopMode?: "normal" | "alternate";
  color?: string;
  className?: string;
  style?: CSSProperties;
  autoPlay?: boolean;
}

export const KpiBoardAnimator = forwardRef(function KpiBoardAnimator(
  props: KpiBoardAnimatorProps,
  ref: ForwardedRef<AnimatorHandle>,
) {
  const settings = useMemo(
    () => ({
      ...defaultOptions,
      ...props,
      chartType: "kpi",
      hold: defaultOptions.hold,
    }),
    [props],
  );

  const {
    frames,
    width,
    height,
    padding,
    duration,
    labels,
    labelFontSize: rawLabelFontSize,
    animateEach,
    loopMode,
    color = "#6c5ce7",
    className,
    style,
    autoPlay = true,
  } = settings;
  const labelFontSize = Number.isFinite(rawLabelFontSize)
    ? rawLabelFontSize
    : defaultOptions.labelFontSize;

  const animationIdRef = useRef(`ds-kpi-${++idCounter}`);
  const baseName = animationIdRef.current;
  const { paused } = useAnimator(ref, autoPlay);

  if (!Array.isArray(frames) || frames.length === 0) {
    throw new Error("frames must be a non-empty array");
  }

  const keys = useMemo(() => {
    const ordered: string[] = [];
    frames.forEach((frame) => {
      Object.keys(frame).forEach((key) => {
        if (key === "label") return;
        if (!ordered.includes(key)) {
          ordered.push(key);
        }
      });
    });
    return ordered;
  }, [frames]);

  // Extract per-item configurations for each key across all frames
  const itemConfigsByKey = useMemo(() => {
    return keys.reduce<
      Record<string, Array<ReturnType<typeof extractItemConfig>>>
    >((acc, key) => {
      acc[key] = frames.map((frame) => extractItemConfig(frame[key]));
      return acc;
    }, {});
  }, [frames, keys]);

  const [frameIndex, setFrameIndex] = useState(0);
  const directionRef = useRef(1);

  const currentLabel = frames[frameIndex]?.label ?? "";
  const hasFrameLabel = Boolean(currentLabel);
  const labelOffset = hasFrameLabel
    ? Math.max(32, Math.round(labelFontSize + 8))
    : 0;
  const columns = Math.max(1, Math.min(3, keys.length));
  const rows = Math.ceil(keys.length / columns);
  const usableWidth = width - padding * 2;
  const usableHeight = Math.max(0, height - padding * 2 - labelOffset);
  const cellWidth = Math.max(24, usableWidth / columns);
  const cellHeight = Math.max(32, usableHeight / rows);

  const frameDuration = duration / Math.max(1, frames.length);
  const tileDelay = animateEach
    ? Math.min(300, frameDuration / Math.max(1, keys.length))
    : 0;
  const cycleDuration =
    animateEach && keys.length > 1
      ? frameDuration + tileDelay * (keys.length - 1)
      : frameDuration;

  useEffect(() => {
    if (paused) return;

    const timer = setInterval(() => {
      setFrameIndex((current) => {
        let next = current + directionRef.current;
        if (loopMode === "normal") {
          if (next >= frames.length) next = 0;
          if (next < 0) next = frames.length - 1;
        } else {
          if (next >= frames.length || next < 0) {
            directionRef.current = directionRef.current * -1;
            next = Math.max(0, Math.min(frames.length - 1, next));
          }
        }
        return next;
      });
    }, cycleDuration);

    return () => clearInterval(timer);
  }, [cycleDuration, frames.length, loopMode, paused]);

  const formatValue = (val: number | string | undefined) => {
    const num = Number(val);
    if (!Number.isFinite(num)) return "—";
    return Number(num.toPrecision(3)).toString();
  };

  const kpiKeyframes = useMemo(() => {
    return (
      `@keyframes ${baseName}-kpi-pop {` +
      "0% { transform: translateY(0) scale(1); opacity: 0; }" +
      "50% { transform: translateY(-3px) scale(1.01); opacity: 1; }" +
      "100% { transform: translateY(0) scale(1); opacity: 1; }" +
      "}"
    );
  }, [baseName]);

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label="Animated KPI board"
      className={className}
      style={{ overflow: "visible", color, ...style }}
    >
      <defs>
        {keys.map((key, index) => {
          const itemConfig = itemConfigsByKey[key]?.[frameIndex];
          const itemStyle = itemConfig?.dataItemStyle ?? "solid";
          const itemColor = itemConfig?.color;

          if (itemStyle !== "striped") return null;

          const patternId = `${baseName}-kpi-stripe-${index}`;
          const stripeColor = itemColor || color;

          return (
            <pattern
              key={patternId}
              id={patternId}
              patternUnits="userSpaceOnUse"
              width="32"
              height="32"
              patternTransform="rotate(45)"
            >
              <rect width="16" height="32" fill={stripeColor} opacity="0.08" />
              <rect
                x="16"
                width="16"
                height="32"
                fill={stripeColor}
                opacity="0.02"
              />
            </pattern>
          );
        })}
      </defs>
      <style>{kpiKeyframes}</style>
      {hasFrameLabel ? (
        <text
          x={padding}
          y={padding + labelFontSize}
          textAnchor="start"
          fontSize={labelFontSize}
          fontWeight={700}
          fill="currentColor"
        >
          {currentLabel}
        </text>
      ) : null}
      <g transform={`translate(${padding}, ${padding + labelOffset})`}>
        {keys.map((key, index) => {
          const col = index % columns;
          const row = Math.floor(index / columns);
          const value = extractValue(frames[frameIndex]?.[key]);
          const delay = animateEach ? tileDelay * index : 0;
          const rectWidth = Math.max(8, cellWidth - 16);
          const rectHeight = Math.max(12, cellHeight - 16);

          // Get per-item config (use current frame's config)
          const itemConfig = itemConfigsByKey[key]?.[frameIndex];
          const itemColor = itemConfig?.color;
          const itemStyle = itemConfig?.dataItemStyle ?? "solid";

          const fillValue =
            itemStyle === "striped"
              ? `url(#${baseName}-kpi-stripe-${index})`
              : "currentColor";

          return (
            <g
              key={`${key}-${frameIndex}`}
              transform={`translate(${(cellWidth * col).toFixed(2)}, ${(cellHeight * row).toFixed(2)})`}
              style={itemColor ? { color: itemColor } : undefined}
            >
              <g
                style={{
                  animationName: `${baseName}-kpi-pop`,
                  animationDuration: "320ms",
                  animationTimingFunction: "ease",
                  animationDelay: `${delay}ms`,
                  animationIterationCount: 1,
                  animationFillMode: "forwards",
                  animationPlayState: paused ? "paused" : "running",
                  opacity: 0,
                }}
              >
                <rect
                  x={8}
                  y={8}
                  width={rectWidth.toFixed(2)}
                  height={rectHeight.toFixed(2)}
                  rx={10}
                  fill={fillValue}
                  opacity={itemStyle === "striped" ? 1 : 0.08}
                />
                <text
                  x={(cellWidth / 2).toFixed(2)}
                  y={(cellHeight / 2 - 4).toFixed(2)}
                  textAnchor="middle"
                  fontSize={12}
                  fill="currentColor"
                >
                  {labels ? key : ""}
                </text>
                <text
                  x={(cellWidth / 2).toFixed(2)}
                  y={(cellHeight / 2 + 24).toFixed(2)}
                  textAnchor="middle"
                  fontSize={22}
                  fontWeight={700}
                  fill="currentColor"
                >
                  {formatValue(value)}
                </text>
              </g>
            </g>
          );
        })}
      </g>
    </svg>
  );
});

export default KpiBoardAnimator;
